import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IDeviceTruckImplement, defaultValue } from 'app/shared/model/device-truck-implement.model';

const initialState: EntityState<IDeviceTruckImplement> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/device-truck-implements';

// Actions

interface IPagedDeviceTruckImplementQueryParams extends IQueryParams {
  nameSearch?: string;
}

interface IUpdateFirmware {
  firmware: string;
  serial: string;
  updateAll: boolean;
}

export const getEntities = createAsyncThunk(
  'deviceTruckImplement/fetch_entity_list',
  async ({ page, size, sort, nameSearch }: IPagedDeviceTruckImplementQueryParams) => {
    const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&nameSearch=${nameSearch}&` : '?'}cacheBuster=${new Date().getTime()}`;
    return axios.get<IDeviceTruckImplement[]>(requestUrl);
  }
);

export const getDeviceTruckImplementList = createAsyncThunk('deviceTruckImplement/fetch_entity_list', async () => {
  const requestUrl = 'api/list-device-truck-implements';
  return axios.get<IDeviceTruckImplement[]>(requestUrl);
});

export const getDeviceByTruckImplementId = createAsyncThunk('deviceTruckImplement/fetch_entity_list', async (truckImplementId: string) => {
  const requestUrl = `api/device-truck-implements-by-truck-id/${truckImplementId}`;
  return axios.get<IDeviceTruckImplement[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'deviceTruckImplement/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IDeviceTruckImplement>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'deviceTruckImplement/create_entity',
  async (entity: IDeviceTruckImplement, thunkAPI) => {
    const result = await axios.post<IDeviceTruckImplement>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'deviceTruckImplement/update_entity',
  async (entity: IDeviceTruckImplement, thunkAPI) => {
    const result = await axios.put<IDeviceTruckImplement>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'deviceTruckImplement/partial_update_entity',
  async (entity: IDeviceTruckImplement, thunkAPI) => {
    const result = await axios.patch<IDeviceTruckImplement>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'deviceTruckImplement/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IDeviceTruckImplement>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateFirmware = createAsyncThunk(
  'deviceTruckImplement/partial_update_entity_firmware',
  async ({ firmware, updateAll, serial }: IUpdateFirmware, thunkAPI) => {
    const result = await axios.patch<IDeviceTruckImplement>(
      `${apiUrl}/update-firmware`,
      {},
      {
        params: {
          firmware,
          updateAll,
          serial,
        },
      }
    );
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const DeviceTruckImplementSlice = createEntitySlice({
  name: 'deviceTruckImplement',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(updateFirmware.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getDeviceByTruckImplementId), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(updateFirmware), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, updateFirmware, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = DeviceTruckImplementSlice.actions;

// Reducer
export default DeviceTruckImplementSlice.reducer;
